import { useState, useEffect } from 'react';
import api from '@/api';
import { FilePdf, MagnifyingGlass, DownloadSimple, ArrowRight, Eye } from '@phosphor-icons/react';
import { formatLabel, STATUS_STYLES, RISK_STYLES, RISK_CHART_COLORS, BADGE, timeAgo } from '@/lib/format';
import { PieChart, Pie, Cell, Legend, ResponsiveContainer } from 'recharts';
import ReportPreviewModal from '@/components/ReportPreviewModal';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const downloadPdf = async (path, filename) => {
  try {
    const resp = await api.get(path, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || `Radar_Report_${Date.now()}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) { alert('PDF download failed. Please try again.'); }
};

export default function ReportsPage() {
  const [orgReport, setOrgReport] = useState(null);
  useEffect(() => { document.title = "Radar — Reports"; }, []);
  const [riskReport, setRiskReport] = useState(null);
  const [cases, setCases] = useState([]);
  const [casesTotal, setCasesTotal] = useState(0);
  const [caseSearch, setCaseSearch] = useState('');
  const [caseStatusFilter, setCaseStatusFilter] = useState('');
  const [caseRiskFilter, setCaseRiskFilter] = useState('');
  const [casePage, setCasePage] = useState(0);
  const [syncHistory, setSyncHistory] = useState([]);
  // Preview modal
  const [preview, setPreview] = useState({ open: false, path: null, title: '', filename: '' });
  const openPreview = (path, title, filename) => setPreview({ open: true, path, title, filename });

  useEffect(() => {
    api.get('/reports/org-summary').then(r => setOrgReport(r.data)).catch(() => {});
    api.get('/reports/risk-profile').then(r => setRiskReport(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = { skip: casePage * 25, limit: 25 };
    if (caseSearch) params.search = caseSearch;
    if (caseStatusFilter) params.status = caseStatusFilter;
    if (caseRiskFilter) params.risk_rating = caseRiskFilter;
    api.get('/cases', { params }).then(r => { setCases(r.data.cases); setCasesTotal(r.data.total); }).catch(() => {});
  }, [caseSearch, caseStatusFilter, caseRiskFilter, casePage]);

  const riskData = riskReport ? Object.entries(riskReport.risk_distribution).filter(([_, v]) => v > 0).map(([k, v]) => ({ name: k, value: v })) : [];

  return (
    <div data-testid="reports-page">
      <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-1">Reports</h1>
      <p className="text-sm text-[#52525B] mb-6">Generate professional compliance reports for regulatory submission</p>

      {/* Top Cards: Org Report + Regulatory Pack */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-white border border-[#E4E4E7] rounded-md p-5">
          <h3 className="font-heading text-base font-semibold mb-1">Organisation Report</h3>
          <p className="text-xs text-[#52525B] mb-4">One-page compliance posture snapshot: total cases, screening rate, clearance rate, risk distribution, cases-by-source. Ideal for board / internal review.</p>
          {orgReport && (
            <div className="grid grid-cols-3 gap-2 mb-4 text-center">
              <div><p className="text-lg font-bold">{orgReport.total_cases}</p><p className="text-[10px] text-[#A1A1AA]">Cases</p></div>
              <div><p className="text-lg font-bold">{orgReport.screening_rate}%</p><p className="text-[10px] text-[#A1A1AA]">Screened</p></div>
              <div><p className="text-lg font-bold">{orgReport.clearance_rate}%</p><p className="text-[10px] text-[#A1A1AA]">Cleared</p></div>
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={() => openPreview('/reports/org-summary/pdf', 'Organisation Report', 'Radar_Organisation_Report.pdf')} data-testid="preview-org-pdf-btn" className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium border border-[#0052FF] text-[#0052FF] rounded-md hover:bg-blue-50 flex-1">
              <Eye size={14} /> Preview
            </button>
            <button onClick={() => downloadPdf('/reports/org-summary/pdf', 'Radar_Organisation_Report.pdf')} data-testid="download-org-pdf-btn" className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC] flex-1">
              <FilePdf size={14} /> Generate Organisation PDF
            </button>
          </div>
        </div>
        <div className="bg-white border border-[#E4E4E7] rounded-md p-5">
          <h3 className="font-heading text-base font-semibold mb-1">Regulatory Pack</h3>
          <p className="text-xs text-[#52525B] mb-4">Full compliance submission: org summary + every high-risk / escalated case with match alerts, analyst remarks &amp; monitoring hits + data-source coverage attestation. Designed for regulators.</p>
          {riskData.length > 0 && (
            <ResponsiveContainer width="100%" height={120}>
              <PieChart><Pie data={riskData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={50} label={false}>
                {riskData.map((e, i) => <Cell key={i} fill={RISK_CHART_COLORS[e.name] || '#ccc'} />)}
              </Pie><Legend wrapperStyle={{ fontSize: '10px' }} /></PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex gap-2 mt-2">
            <button onClick={() => openPreview('/reports/regulatory-pack/pdf', 'Regulatory Pack', 'Radar_Regulatory_Pack.pdf')} data-testid="preview-reg-pack-btn" className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium border border-[#0052FF] text-[#0052FF] rounded-md hover:bg-blue-50 flex-1">
              <Eye size={14} /> Preview
            </button>
            <button onClick={() => downloadPdf('/reports/regulatory-pack/pdf', 'Radar_Regulatory_Pack.pdf')} data-testid="download-reg-pack-btn" className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC] flex-1">
              <FilePdf size={14} /> Generate Regulatory Pack
            </button>
          </div>
        </div>
      </div>

      {/* Case Reports — Full case list with PDF buttons */}
      <div className="mb-8">
        <h3 className="font-heading text-base font-semibold mb-3">Case Reports</h3>
        <div className="flex gap-3 mb-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <input value={caseSearch} onChange={e => { setCaseSearch(e.target.value); setCasePage(0); }} placeholder="Search cases..." className="w-full pl-8 pr-3 py-2 text-sm border border-[#E4E4E7] rounded-md" />
          </div>
          <select value={caseStatusFilter} onChange={e => { setCaseStatusFilter(e.target.value); setCasePage(0); }} className="px-3 py-2 text-sm border border-[#E4E4E7] rounded-md">
            <option value="">All Statuses</option>
            {['draft','pending_screening','under_review','cleared','escalated','rejected','suspended'].map(s => <option key={s} value={s}>{formatLabel(s)}</option>)}
          </select>
          <select value={caseRiskFilter} onChange={e => { setCaseRiskFilter(e.target.value); setCasePage(0); }} className="px-3 py-2 text-sm border border-[#E4E4E7] rounded-md">
            <option value="">All Risk Levels</option>
            {['Low','Medium','High','Very High','Prohibited'].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
          <table className="w-full">
            <thead><tr className="border-b border-[#E4E4E7] bg-[#F7F7F8]">
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Name</th>
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Type</th>
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Status</th>
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Risk</th>
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-[#52525B]">Report</th>
            </tr></thead>
            <tbody>
              {cases.map(c => (
                <tr key={c.id} className="border-b border-[#E4E4E7] hover:bg-[#F7F7F8]">
                  <td className="px-4 py-2.5 text-sm font-medium">{c.full_name}</td>
                  <td className="px-4 py-2.5 text-xs">{formatLabel(c.case_type)}</td>
                  <td className="px-4 py-2.5"><span className={STATUS_STYLES[c.status] || ''}>{formatLabel(c.status)}</span></td>
                  <td className="px-4 py-2.5"><span className={RISK_STYLES[c.risk_rating] || ''}>{c.risk_rating}</span></td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-1.5">
                      <button onClick={() => openPreview(`/reports/case/${c.id}/pdf`, `Case Report — ${c.full_name}`, `Radar_${(c.full_name||'Case').replace(/ /g,'_')}.pdf`)} data-testid={`preview-case-pdf-${c.id}`} className="flex items-center gap-1 px-2 py-1 text-xs border border-[#0052FF] text-[#0052FF] rounded hover:bg-blue-50" title="Preview">
                        <Eye size={12} />
                      </button>
                      <button onClick={() => downloadPdf(`/reports/case/${c.id}/pdf`, `Radar_${(c.full_name||'Case').replace(/ /g,'_')}.pdf`)} data-testid={`download-case-pdf-${c.id}`} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-[#0052FF] text-white rounded hover:bg-[#0040CC]">
                        <FilePdf size={12} /> PDF
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {cases.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-[#A1A1AA]">No cases available. Create your first case to generate reports.</td></tr>}
            </tbody>
          </table>
          <div className="px-4 py-2.5 border-t border-[#E4E4E7] flex items-center justify-between">
            <span className="text-xs text-[#A1A1AA]">Showing {casePage * 25 + 1}–{Math.min((casePage + 1) * 25, casesTotal)} of {casesTotal}</span>
            <div className="flex gap-2">
              <button onClick={() => setCasePage(p => Math.max(0, p - 1))} disabled={casePage === 0} className="px-3 py-1 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1] disabled:opacity-40">Prev</button>
              <button onClick={() => setCasePage(p => p + 1)} disabled={(casePage + 1) * 25 >= casesTotal} className="px-3 py-1 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1] disabled:opacity-40">Next</button>
            </div>
          </div>
        </div>
      </div>

      {/* Monitoring Report */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-base font-semibold">Monitoring Report</h3>
          <div className="flex gap-2">
            <button onClick={() => openPreview('/reports/org-summary/pdf', 'Monitoring Summary', 'Radar_Monitoring_Summary.pdf')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#0052FF] text-[#0052FF] rounded-md hover:bg-blue-50"><Eye size={14} /> Preview</button>
            <button onClick={() => downloadPdf('/reports/org-summary/pdf', 'Radar_Monitoring_Summary.pdf')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1]"><FilePdf size={14} /> Monitoring Summary PDF</button>
          </div>
        </div>
        {orgReport && (
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-white border border-[#E4E4E7] rounded-md p-4"><p className="text-xs text-[#52525B]">Monitored Cases</p><p className="text-xl font-bold">{orgReport.monitored_cases}</p></div>
            <div className="bg-white border border-[#E4E4E7] rounded-md p-4"><p className="text-xs text-[#52525B]">Pending Hits</p><p className="text-xl font-bold text-red-600">{orgReport.pending_monitoring_hits}</p></div>
            <div className="bg-white border border-[#E4E4E7] rounded-md p-4"><p className="text-xs text-[#52525B]">Total Screenings</p><p className="text-xl font-bold">{orgReport.total_screenings}</p></div>
            <div className="bg-white border border-[#E4E4E7] rounded-md p-4"><p className="text-xs text-[#52525B]">Match Rate</p><p className="text-xl font-bold">{orgReport.screenings_with_matches}/{orgReport.total_screenings}</p></div>
          </div>
        )}
      </div>

      {orgReport && <p className="text-xs text-[#A1A1AA]">Report data as of {orgReport.generated_at ? new Date(orgReport.generated_at).toLocaleString() : 'now'}</p>}

      <ReportPreviewModal
        open={preview.open}
        path={preview.path}
        title={preview.title}
        filename={preview.filename}
        onClose={() => setPreview({ open: false, path: null, title: '', filename: '' })}
      />
    </div>
  );
}
